import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SEARCH_PAGES } from '../config/searchPages';
import { searchProducts } from '../api/search';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const normalize = (s) => (s || '').toLowerCase().trim();

export default function SearchResults() {
  const qs = useQuery();
  const q = qs.get('q') || '';

  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [err, setErr] = useState('');

  const pageMatches = useMemo(() => {
    const term = normalize(q);
    if (!term) return [];
    return SEARCH_PAGES.filter((p) => normalize(p.title).includes(term)).slice(0, 20);
  }, [q]);

  useEffect(() => {
    let alive = true;

    async function run() {
      const term = q.trim();
      if (!term) {
        setProducts([]);
        setErr('');
        return;
      }

      setLoading(true);
      setErr('');
      try {
        const list = await searchProducts(term);
        if (!alive) return;
        setProducts(Array.isArray(list) ? list : []);
      } catch {
        if (!alive) return;
        setErr('Product search is unavailable right now.');
        setProducts([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [q]);

  return (
    <>
      <Header />
      <main className="rbt-main-wrapper" style={{ paddingTop: 20 }}>
        <div className="container">
          <div className="rbt-section-gap">
            <h3 style={{ marginBottom: 6 }}>Search</h3>
            <div style={{ color: '#666', marginBottom: 18 }}>
              Results for: <strong>{q || '-'}</strong>
            </div>

            <div className="row g-4">
              {/* Shirts */}
              <div className="col-lg-8">
                <div className="rbt-card" style={{ padding: 18 }}>
                  <h5 style={{ marginBottom: 12 }}>Shirts</h5>

                  {loading && <div style={{ color: '#666' }}>Searching…</div>}
                  {!loading && err && <div style={{ color: '#b00020' }}>{err}</div>}

                  {!loading && !err && products.length === 0 && (
                    <div style={{ color: '#666' }}>No shirts found.</div>
                  )}

                  <div className="row g-3">
                    {products.map((p) => {
                      const id = p.id;
                      const title = p.title || 'Product';
                      const img = p.imageUrl || '/assets/images/product/1.jpg';
                      const price = p.defaultTotalPrice;

                      return (
                        <div className="col-md-6" key={id}>
                          <Link
                            to={`/product/${id}`}
                            style={{
                              display: 'flex',
                              gap: 12,
                              border: '1px solid #eee',
                              borderRadius: 10,
                              padding: 12,
                              textDecoration: 'none',
                              color: 'inherit',
                            }}
                          >
                            <img
                              src={img}
                              alt={title}
                              style={{
                                width: 70,
                                height: 70,
                                objectFit: 'cover',
                                borderRadius: 10,
                                background: '#f7f7f7',
                              }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {title}
                              </div>

                              <div style={{ marginTop: 6, color: '#444' }}>
                                ${Number(price || 0).toFixed(2)}
                              </div>

                              {p.matchedFabricId && (
                                <div style={{ marginTop: 4, fontSize: 12, color: '#777' }}>
                                  Matched FabricID: {p.matchedFabricId}
                                </div>
                              )}
                            </div>
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Pages */}
              <div className="col-lg-4">
                <div className="rbt-card" style={{ padding: 18 }}>
                  <h5 style={{ marginBottom: 12 }}>Pages</h5>

                  {pageMatches.length === 0 ? (
                    <div style={{ color: '#666' }}>No pages found.</div>
                  ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {pageMatches.map((p) => (
                        <li
                          key={p.path}
                          style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}
                        >
                          <Link to={p.path} style={{ textDecoration: 'none' }}>
                            {p.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
